import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";

/* reuse the head-office theme, shell styles, widgets and skeletons verbatim so
   the campus dashboard is a pixel-for-pixel sibling — only the data + a few
   labels differ. */
import themeStyles from "../headOffice/theme/theme.module.scss";
import styles from "../headOffice/style.module.scss";
import {
  attrFor,
  useHeadOfficeTheme,
} from "../headOffice/theme/HeadOfficeThemeContext";
import { hoFontStyle, hoFontVariables } from "../headOffice/theme/fonts";

import BrandingBand from "../headOffice/BrandingBand";
import DashboardControls from "../headOffice/DashboardControls";
import KpiStrip from "../headOffice/KpiStrip";
import AssignmentPipeline from "../headOffice/AssignmentPipeline";
import AdoptionGauge from "../headOffice/AdoptionGauge";
import ReaderCampuses from "../headOffice/ReaderCampuses";
import CampusComparison from "../headOffice/CampusComparison";
import TopTeachers from "../headOffice/TopTeachers";
import TopStudents from "../headOffice/TopStudents";
import BooksContentAnalytics from "../headOffice/BooksContentAnalytics";
import TeacherAdoption from "../headOffice/TeacherAdoption";
import WidgetEmpty from "./WidgetEmpty";
import CustomizeWidgetsModal from "../headOffice/modals/CustomizeWidgetsModal";
import DrillDownModal from "../headOffice/modals/DrillDownModal";
import ExportOptionsModal from "../headOffice/modals/ExportOptionsModal";
import type { BlurTarget } from "../headOffice/_shared/exportPdf";
import {
  ModalData,
  buildTeacherModal,
  classroomCertificate,
  classroomLink,
  studentCertificate,
  studentProfileLink,
  teacherCertificate,
  teacherProfileLink,
} from "../headOffice/modals/modalBuilders";
import { DateRange, defaultDateRange } from "../headOffice/_shared/DateRangePicker";
import {
  KpiSkeleton,
  GrowthSkeleton,
  MixSkeleton,
  CampusesSkeleton,
  AdoptionSkeleton,
  ListSkeleton,
  ContentSkeleton,
  ComparisonSkeleton,
} from "../headOffice/Skeleton";

import {
  COMPARISON_COLS,
  CampusDatum,
  StudentDatum,
  TeacherDatum,
  VIEW_ALL_PAGE_SIZE,
} from "../headOffice/data";
import { CAMPUS_WIDGET_META, WidgetKey } from "./data";
import { useCampusData } from "./useCampusData";
import {
  getReaderClass,
  getReaderClassDetail,
  getGradeDrilldown,
  getStudentDetail,
  getTeacherDetail,
  getTopTeachers,
  getTopStudents,
  getBookContentAnalysis,
  getTeacherAdoption,
  getClassComparison,
} from "shared/services/campusAnalyticsService";
import {
  buildClassModalFromParts,
  buildStudentModalFromRow,
  mapComparison,
  mapReaderClasses,
  mapContent,
  mapTeacherAdoption,
  mapTopStudents,
  mapTopTeachers,
} from "./apiMappers";
import {
  buildStudentModalFromDetail,
  buildTeacherModalFromDetail,
} from "../headOffice/apiMappers";
import type { ViewAllPage } from "../headOffice/modals/ViewAllModal";

/* The school app defers the two canvas widgets with next/dynamic because they
   must not render on the server. The admin panel is client-only, so they are
   imported directly. */
import ReadingGrowthTrends from "../headOffice/ReadingGrowthTrends";
import ReadingMix from "../headOffice/ReadingMix";

interface CampusDashboardProps {
  /** campus display name for the branding band */
  schoolName?: string;
  /** branding band subtitle — "Campus Dashboard" (campus admin) or
   *  "School Dashboard" (a standalone individual school). */
  subtitle?: string;
  /** When set, every analytics call is scoped to this campus instead of the
   *  logged-in account — that is how a head office views one campus on
   *  /schools/campusDetail/:id. Stable for the lifetime of the page. */
  campusId?: number | string;
}

const defaultShow = () => {
  const s = {} as Record<WidgetKey, boolean>;
  CAMPUS_WIDGET_META.forEach((w) => (s[w.key] = true));
  return s;
};

/** The first 5 widgets are fixed — they can never be hidden via Customize. */
const FIXED_WIDGETS: WidgetKey[] = CAMPUS_WIDGET_META.slice(0, 5).map(
  (w) => w.key,
);

/** unwrap the `{ ..., data }` envelope + pagination for a View-All page */
const pageOf = (res: any, mapRows: (d: any) => any[], page: number) => {
  const d = res?.data?.data ?? {};
  const p = d?.pagination ?? {};
  return {
    rows: mapRows(d),
    total: Number(p.total) || 0,
    lastPage: Number(p.last_page) || 1,
    currentPage: Number(p.current_page) || page,
  };
};

export default function CampusDashboard({
  schoolName = "Campus",
  subtitle = "Campus Dashboard",
  campusId,
}: CampusDashboardProps) {
  const { themeKey } = useHeadOfficeTheme();

  const [show, setShow] = useState<Record<WidgetKey, boolean>>(defaultShow);
  const [range, setRange] = useState<DateRange>(defaultDateRange());
  const [mixIdx, setMixIdx] = useState(0);
  const [growthSel, setGrowthSel] = useState<boolean[]>([]);
  const [modal, setModal] = useState<ModalData | null>(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, loading, error, refetch } = useCampusData(range, campusId);
  // block Export until every widget has finished loading
  const isLoadingAny = Object.values(loading).some(Boolean);
  const {
    kpis,
    readerClasses,
    readerMaxBooks,
    teachers,
    students,
    adoption,
    mixDimensions,
    content,
    assignment,
    comparison,
    teacherAdoption,
    growthRange,
    growthMetrics,
  } = data;

  // keep the growth metric selection sized to what the API returned
  useEffect(() => {
    setGrowthSel((prev) =>
      prev.length === growthMetrics.length
        ? prev
        : growthMetrics.map((_, i) => i === 0),
    );
  }, [growthMetrics.length]);

  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2800);
  };

  /* badge counts only the widgets the modal can actually toggle (the first 5
     are fixed), so it always matches what the user sees in there */
  const activeCount = CAMPUS_WIDGET_META.slice(FIXED_WIDGETS.length).filter(
    (m) => show[m.key],
  ).length;
  const toggleWidget = (key: WidgetKey) => {
    if (FIXED_WIDGETS.includes(key)) return; // first 5 always stay visible
    setShow((s) => ({ ...s, [key]: !s[key] }));
  };
  const resetWidgets = () => setShow(defaultShow());
  const toggleGrowth = (i: number) =>
    setGrowthSel((sel) => {
      const next = sel.slice();
      next[i] = !next[i];
      if (!next.some(Boolean)) next[i] = true; // keep at least one
      return next;
    });
  // every downstream request (pagers + drill-downs) inherits the campus scope
  const win: any = campusId ? { ...range, campus_id: campusId } : range;

  /* ---- View-All pagers (hit the campus endpoints, not head office) ---- */
  const teacherPager = useCallback(
    (page: number) =>
      getTopTeachers({ ...win, per_page: VIEW_ALL_PAGE_SIZE, page }).then((r) =>
        pageOf(r, mapTopTeachers, page),
      ),
    [win.from, win.to],
  );
  const studentPager = useCallback(
    (page: number) =>
      getTopStudents({ ...win, per_page: VIEW_ALL_PAGE_SIZE, page }).then((r) =>
        pageOf(r, mapTopStudents, page),
      ),
    [win.from, win.to],
  );
  const contentPager = useCallback(
    (page: number) =>
      getBookContentAnalysis({ ...win, per_page: VIEW_ALL_PAGE_SIZE, page }).then((r) =>
        pageOf(r, (d) => mapContent(d).campuses, page),
      ),
    [win.from, win.to],
  );
  const teacherAdoptionPager = useCallback(
    (page: number) =>
      getTeacherAdoption({ ...win, per_page: VIEW_ALL_PAGE_SIZE, page }).then((r) =>
        pageOf(r, mapTeacherAdoption, page),
      ),
    [win.from, win.to],
  );
  /* Reader Classes "View All" — same endpoint as the card. Adoption is merged
     in from class-comparison for the same page, mirroring `readerRows`. */
  const readerClassPager = useCallback(
    async (page: number): Promise<ViewAllPage> => {
      const [classRes, cmpRes] = await Promise.all([
        getReaderClass({ ...win, per_page: VIEW_ALL_PAGE_SIZE, page }),
        getClassComparison({ ...win, per_page: VIEW_ALL_PAGE_SIZE, page }).catch(
          () => null,
        ),
      ]);
      const d = classRes?.data?.data ?? {};
      const p = d?.pagination ?? {};
      const adoptionById = new Map<number, number>();
      mapComparison(cmpRes?.data?.data ?? {}).forEach((c) => {
        if (c.campus_id != null) adoptionById.set(c.campus_id, c.adoption);
      });
      return {
        rows: mapReaderClasses(d).classes.map((c) =>
          c.campus_id != null && adoptionById.has(c.campus_id)
            ? { ...c, adoption: adoptionById.get(c.campus_id) as number }
            : c,
        ),
        total: Number(p.total) || 0,
        lastPage: Number(p.last_page) || 1,
        currentPage: Number(p.current_page) || page,
      };
    },
    [win.from, win.to],
  );

  const comparisonPager = useCallback(
    (page: number) =>
      getClassComparison({ ...win, per_page: VIEW_ALL_PAGE_SIZE, page }).then((r) =>
        pageOf(r, mapComparison, page),
      ),
    [win.from, win.to],
  );

  /* ---- drill-downs ---- */
  const openClass = async (c: CampusDatum, rank = 0) => {
    if (!c.campus_id) return;
    // only the campus's top classroom is certified
    const certificate = rank === 1 ? classroomCertificate(c, win) : undefined;
    showToast("Loading classroom…");
    try {
      const [detailRes, studentsRes, healthRes, teacherRes] = await Promise.all([
        getReaderClassDetail(c.campus_id, win),
        getGradeDrilldown(c.campus_id, { type: "student", ...win, per_page: 5, page: 1 }),
        getGradeDrilldown(c.campus_id, { type: "class-health", ...win }),
        getGradeDrilldown(c.campus_id, { type: "teacher", ...win }),
      ]);
      setModal({
        ...buildClassModalFromParts(
          detailRes?.data?.data,
          studentsRes?.data?.data,
          healthRes?.data?.data,
          teacherRes?.data?.data,
        ),
        // `campus_id` carries the class id for these rows — see mapReaderClasses
        profile: classroomLink(c.campus_id),
        certificate,
      });
    } catch (e) {
      showToast("Couldn't load classroom details");
    }
  };
  // Teacher drill-down: fetch the per-teacher detail (school/teacher/{id}), then
  // build the same modal as head office. Falls back to the row data on error.
  const openTeacher = async (t: TeacherDatum, rank = 0) => {
    // link straight through to the teacher's own school-panel page
    const profile = teacherProfileLink(t.teacher_id);
    // only the campus's top teacher is certified
    const certificate = rank === 1 ? teacherCertificate(t, win) : undefined;
    if (!t.teacher_id) {
      setModal({ ...buildTeacherModal(t), profile, certificate });
      return;
    }
    showToast("Loading teacher…");
    try {
      const res = await getTeacherDetail(t.teacher_id, win);
      const detail = res?.data?.data;
      setModal({
        ...(detail
          ? buildTeacherModalFromDetail(detail, t.name)
          : buildTeacherModal(t)),
        profile,
        certificate,
      });
    } catch (e) {
      setModal({ ...buildTeacherModal(t), profile, certificate });
    }
  };

  // Student drill-down: fetch the per-student detail (school/student/{id}), then
  // build the same modal as head office. Falls back to the row data on error.
  const openStudent = async (s: StudentDatum, rank = 0) => {
    // link straight through to the student's own school-panel page
    const profile = studentProfileLink(s.student_id);
    // only the campus's top reader is certified
    const certificate = rank === 1 ? studentCertificate(s, win) : undefined;
    if (!s.student_id) {
      setModal({ ...buildStudentModalFromRow(s), profile, certificate });
      return;
    }
    showToast("Loading student…");
    try {
      const res = await getStudentDetail(s.student_id, win);
      const detail = res?.data?.data;
      setModal({
        ...(detail
          ? buildStudentModalFromDetail(detail)
          : buildStudentModalFromRow(s)),
        profile,
        certificate,
      });
    } catch (e) {
      setModal({ ...buildStudentModalFromRow(s), profile, certificate });
    }
  };

  // Export asks first what to blur — a school has no campuses, so that choice
  // is left out here.
  const openExport = () => {
    if (isLoadingAny) return; // wait until every widget has data
    setModal(null);
    setExportOpen(true);
  };
  const exportPdf = async (blur: BlurTarget[]) => {
    setExportOpen(false);
    showToast("Generating export…");
    await new Promise((r) => setTimeout(r, 300));
    try {
      const el = document.getElementById("campus-report-root");
      if (!el) return;
      const { exportElementToPdf } = await import(
        "../headOffice/_shared/exportPdf"
      );
      // a blurred school name shouldn't survive in the file name either
      const fileName =
        blur.includes("school")
          ? "Campus Report.pdf"
          : `${schoolName} — Campus Report.pdf`;
      await exportElementToPdf(el, fileName, {
        excludeWidgets: [],
        blur,
      });
      showToast("Export downloaded (PDF)");
    } catch (e) {
      console.error("PDF export failed", e);
      showToast("Couldn't export the dashboard");
    }
  };
  const generateCertificate = () => showToast("Certificate generated (PDF)");

  const mixActiveIdx = mixDimensions.length
    ? Math.min(mixIdx, mixDimensions.length - 1)
    : 0;

  // `reader-class-v1` has no per-class adoption, but `class-comparison-v1` does.
  // Merge it in by class id so the second bar shows real Adoption.
  const readerRows = useMemo(() => {
    const adoptionById = new Map<number, number>();
    comparison.forEach((c) => {
      if (c.campus_id != null) adoptionById.set(c.campus_id, c.adoption);
    });
    return readerClasses.map((c) =>
      c.campus_id != null && adoptionById.has(c.campus_id)
        ? { ...c, adoption: adoptionById.get(c.campus_id) as number }
        : c,
    );
  }, [readerClasses, comparison]);

  // Show whatever rows the API returns (even zeros for a slow period); only
  // fall back to the not-found state when a response is genuinely empty.
  // (Adoption is empty only when there are no enrolled students at all.)
  const hasReaders = readerClasses.length > 0;
  const hasContent = !!content && content.campuses.length > 0;
  const hasAdoption = !!adoption && adoption.enrolled > 0;
  const hasComparison = comparison.length > 0;
  const hasTeacherAdoption = teacherAdoption.length > 0;
  const hasGrowth = growthMetrics.length > 0 && !!growthRange;

  const cell = (
    key: WidgetKey,
    span: string,
    order: number,
    node: React.ReactNode,
    stretch = false,
  ) =>
    show[key] && node ? (
      <div
        key={key}
        data-ho-widget={key}
        className={classNames(styles.cell, span, stretch && styles.stretch)}
        style={{ order }}
      >
        {node}
      </div>
    ) : null;

  return (
    <div
      id="campus-report-root"
      className={classNames(themeStyles.scope, styles.dashboard, hoFontVariables)}
      style={hoFontStyle}
      data-ho-theme={attrFor(themeKey)}
    >
      <div className={styles.topRow}>
        <BrandingBand name={schoolName} subtitle={subtitle} />
        <DashboardControls
          range={range}
          onRangeChange={setRange}
          activeCount={activeCount}
          onCustomize={() => setCustomizeOpen(true)}
          onExport={openExport}
          exportDisabled={isLoadingAny}
        />
      </div>

      {error ? (
        <div className={styles.stateBox}>
          <span>{error}</span>
          <button type="button" className={styles.retryBtn} onClick={() => refetch()}>
            Retry
          </button>
        </div>
      ) : (
        <div className={styles.grid}>
          {cell(
            "kpi",
            styles.span12,
            1,
            loading.kpi ? (
              <KpiSkeleton />
            ) : kpis.length ? (
              <KpiStrip kpis={kpis} />
            ) : (
              <WidgetEmpty title="Campus at a Glance" />
            ),
          )}

          {cell(
            "growth",
            styles.span8,
            2,
            loading.growth ? (
              <GrowthSkeleton />
            ) : !hasGrowth ? (
              <WidgetEmpty
                title="Reading Growth Trends"
                subtitle="Books & pages completed over time"
              />
            ) : growthRange && growthSel.length === growthMetrics.length ? (
              <ReadingGrowthTrends
                range={growthRange}
                metrics={growthMetrics}
                selected={growthSel}
                onToggle={toggleGrowth}
                themeKey={themeKey}
              />
            ) : (
              <GrowthSkeleton />
            ),
            true,
          )}


          {cell(
            "mix",
            styles.span4,
            3,
            loading.mix ? (
              <MixSkeleton />
            ) : mixDimensions.length > 0 ? (
              <ReadingMix
                dimensions={mixDimensions}
                activeIdx={mixActiveIdx}
                onChange={setMixIdx}
              />
            ) : (
              <WidgetEmpty title="Reading Mix" subtitle="By genre & language" />
            ),
            true,
          )}

          {cell(
            "campuses",
            styles.span8,
            4,
            loading.campuses ? (
              <CampusesSkeleton />
            ) : hasReaders ? (
              <ReaderCampuses
                campuses={readerRows.slice(0, 4)}
                maxBooks={readerMaxBooks}
                onOpenCampus={openClass}
                title="The Reader Classrooms"
                subtitle="Every classroom ranked by reading performance — tap a row to drill into students & the classroom teacher"
                entityLabel="Classroom"
                metaFor={(c) => c.teacher || "No teacher"}
                metaBlur="teacher"
                showStudents
                secondaryBar
                secondaryLabel="Adoption"
                fetchPage={readerClassPager}
                viewAllSubtitle="This campus — all classrooms"
              />
            ) : (
              <WidgetEmpty
                title="The Reader Classrooms"
                subtitle="Every classroom ranked by reading performance"
                hint="No classrooms have reading activity in this period yet."
              />
            ),
            true,
          )}

          {cell(
            "adoption",
            styles.span4,
            5,
            loading.adoption ? (
              <AdoptionSkeleton />
            ) : hasAdoption && adoption ? (
              <AdoptionGauge
                percent={adoption.percent}
                caption={adoption.caption}
                deltaPct={adoption.deltaPct}
                deltaLabel="vs previous period"
                ageAppropriate={adoption.ageAppropriate}
              />
            ) : (
              <WidgetEmpty title="Adoption Rate" />
            ),
            true,
          )}

          {cell(
            "teachers",
            styles.span6,
            6,
            loading.teachers ? (
              <ListSkeleton rows={4} />
            ) : teachers.length ? (
              <TopTeachers
                teachers={teachers.slice(0, 4)}
                win={win}
                viewAllSubtitle="This campus — all teachers"
                onOpenTeacher={openTeacher}
                fetchPage={teacherPager}
              />
            ) : (
              <WidgetEmpty title="Top Teachers" />
            ),
          )}

          {cell(
            "students",
            styles.span6,
            7,
            loading.students ? (
              <ListSkeleton rows={4} />
            ) : students.length ? (
              <TopStudents
                students={students.slice(0, 4)}
                onOpenStudent={openStudent}
                win={win}
                viewAllSubtitle="This campus — all students"
                fetchPage={studentPager}
              />
            ) : (
              <WidgetEmpty title="Top Students" />
            ),
          )}


          {/* Books & Content Analytics hidden — Assignment Pipeline takes this slot.
          {cell(
            "content",
            styles.span6,
            8,
            loading.content ? (
              <ContentSkeleton />
            ) : hasContent && content ? (
              <BooksContentAnalytics
                summary={content.summary}
                campuses={content.campuses}
                maxBooks={content.maxBooks}
                win={win}
                subtitle="What the campus is reading, and whether it fits"
                barsTitle="Book completions by classroom"
                fetchPage={contentPager}
              />
            ) : (
              <WidgetEmpty
                title="Books & Content Analytics"
                subtitle="What the campus is reading, and whether it fits"
              />
            ),
            true,
          )}
          */}

          {cell(
            "teacherAdoption",
            styles.span6,
            9,
            loading.teacherAdoption ? (
              <ListSkeleton rows={5} />
            ) : hasTeacherAdoption ? (
              <TeacherAdoption
                campuses={teacherAdoption}
                win={win}
                subtitle="How each teacher's classroom is performing"
                entityLabel="Teacher"
                nameBlur="teacher"
                metricLabel="Quiz pass"
                fracFor={(c) => (c.quiz || 0) / 100}
                valueFor={(c) => `${c.quiz}%`}
                fetchPage={teacherAdoptionPager}
              />
            ) : (
              <WidgetEmpty
                title="Teacher Adoption"
                subtitle="How each teacher's classroom is performing"
              />
            ),
            true,
          )}

          {cell(
            "comparison",
            styles.span12,
            10,
            loading.comparison ? (
              <ComparisonSkeleton />
            ) : hasComparison ? (
              <CampusComparison
                cols={COMPARISON_COLS}
                campuses={comparison}
                win={win}
                title="Classroom Comparison"
                subtitle="Key metrics side by side, by classroom"
                fetchPage={comparisonPager}
              />
            ) : (
              <WidgetEmpty
                title="Classroom Comparison"
                subtitle="Key metrics side by side, by classroom"
              />
            ),
            true,
          )}

          {/* Assignment Pipeline — left of Teacher Adoption, in the slot the
              Books & Content Analytics card used to occupy. Data rides on
              book-content; toggleable via Customize Widgets. */}
          {show.assignment && (
            <div
              data-ho-widget="assignment"
              className={classNames(styles.cell, styles.span6, styles.stretch)}
              style={{ order: 8 }}
            >
              {loading.content ? (
                <MixSkeleton />
              ) : assignment ? (
                <AssignmentPipeline
                  statuses={assignment.statuses}
                  total={assignment.total}
                  donePct={assignment.donePct}
                />
              ) : (
                <WidgetEmpty
                  title="Assignment Pipeline"
                  subtitle="Assignment status for this campus"
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* ---------------- Modals ---------------- */}
      {modal && (
        <DrillDownModal
          data={modal}
          onClose={() => setModal(null)}
          onGenerateReport={openExport}
          onGenerateCertificate={generateCertificate}
        />
      )}

      {exportOpen && (
        <ExportOptionsModal
          onClose={() => setExportOpen(false)}
          onExport={exportPdf}
        />
      )}

      {customizeOpen && (
        <CustomizeWidgetsModal
          show={show}
          widgets={CAMPUS_WIDGET_META}
          onToggle={toggleWidget}
          onReset={resetWidgets}
          onClose={() => setCustomizeOpen(false)}
        />
      )}

      {/* ---------------- Toast ---------------- */}
      {toast && (
        <div className={classNames(styles.toast, "ho-no-print")}>
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M8.5 12.5 11 15l4.5-5" />
          </svg>
          {toast}
        </div>
      )}
    </div>
  );
}
